import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import RetryImg from "@/components/photos/RetryImg";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, Loader2, RotateCcw, Calendar, MapPin, User, MoveRight, ArrowLeft } from "lucide-react";

/**
 * Full-size photo preview + approve/reject controls.
 *
 * Office-side workflow:
 *   - admin / pm see Approve / Reject / Revert controls.
 *   - everyone else sees a read-only banner with the current decision.
 *
 * Rejection requires a reason — the message threads through to the field
 * uploader as an in-app notification so they know what to retake.
 *
 * Admin/PM also get a "Move to different section" button that flips the
 * side rail into a picker over the current template's categories. Used
 * when a photo was uploaded to the wrong requirement slot and needs to
 * be re-tagged without deleting/re-uploading.
 */
export default function PhotoReviewDialog({
  projectId,
  photo,
  photoProgress,
  open,
  onOpenChange,
  onReviewed,
}) {
  const { user } = useAuth();
  const canReview = user && ["admin", "pm"].includes(user.role);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null); // "approve" | "reject" | "revert" | "move" | null
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [movePickerOpen, setMovePickerOpen] = useState(false);
  const [movePicked, setMovePicked] = useState(null);

  // Reset transient form state every time we surface a new photo.
  useEffect(() => {
    setReason("");
    setRejectionOpen(false);
    setMovePickerOpen(false);
    setMovePicked(null);
  }, [photo?.id]);

  if (!photo) return null;

  const submit = async (status) => {
    if (status === "rejected" && !reason.trim()) {
      toast.error("Add a rejection reason so the field crew knows what to fix.");
      return;
    }
    setBusy(status === "approved" ? "approve" : status === "rejected" ? "reject" : "revert");
    try {
      await api.post(
        `/projects/${projectId}/photos/${photo.id}/review`,
        { status, reason: reason.trim() || null },
      );
      const verb = status === "approved" ? "approved" : status === "rejected" ? "rejected" : "reset to pending";
      toast.success(`Photo ${verb}.`);
      onReviewed?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Review failed");
    } finally {
      setBusy(null);
    }
  };

  const submitMove = async () => {
    if (!movePicked) return;
    setBusy("move");
    try {
      await api.post(`/projects/${projectId}/photos/recategorize`, {
        photo_ids: [photo.id],
        category: movePicked.category,
        requirement_key: movePicked.key,
      });
      toast.success(`Photo moved to "${movePicked.label}".`);
      onReviewed?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    } finally {
      setBusy(null);
    }
  };

  const status = photo.review_status || "pending";
  const reviewedAtPretty = photo.reviewed_at
    ? new Date(photo.reviewed_at).toLocaleString()
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-slate-800 max-w-5xl p-0 overflow-hidden"
        data-testid="photo-review-dialog"
      >
        {/* Radix requires a DialogTitle + Description for screen
            readers. We render our own visible header inside the side
            rail (photo requirement + status banner), so the primary
            ones here are visually hidden via inline styles but still
            announced. Using inline sr-only styles (rather than the
            <VisuallyHidden asChild> wrapper) so Radix's mount-time
            invariant check reliably finds a DialogTitle child even
            when the wrapper's asChild forwarding is racing. */}
        <DialogTitle className="sr-only">
          Review photo: {photo.requirement_key || photo.original_filename || "photo"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Approve, reject, or move this photo to a different requirement slot.
        </DialogDescription>
        <div className="grid md:grid-cols-[1.4fr,1fr] gap-0">
          {/* Image preview — sits on a near-black panel so any
              under-exposed field shot stays readable. */}
          <div className="bg-black flex items-center justify-center min-h-[60vh] max-h-[85vh] overflow-hidden">
            {photo.is_video ? (
              <video
                key={photo.id}
                src={fileUrl(photo.id)}
                poster={photo.poster_path ? fileUrl(photo.id, { size: "medium" }) : undefined}
                controls
                playsInline
                preload="metadata"
                className="object-contain w-full max-h-[85vh]"
                data-testid="photo-review-video"
              />
            ) : (
            <RetryImg
              src={fileUrl(photo.id, { size: "medium" })}
              alt={photo.requirement_key || photo.original_filename}
              className="object-contain w-full h-full min-h-[60vh]"
              testId="photo-review-image"
            />
            )}
          </div>

          {/* Side rail: metadata + status + actions */}
          <div className="p-5 flex flex-col gap-4 max-h-[85vh] overflow-y-auto">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                {photo.category || "Photo"}
              </div>
              <h2 className="font-display font-bold text-xl leading-tight">
                {(photo.requirement_key || photo.original_filename || "Photo")
                  .replaceAll("_", " ")}
              </h2>
            </div>

            <ReviewStatusBanner
              status={status}
              reason={photo.review_reason}
              reviewedByName={photo.reviewed_by_name}
              reviewedAt={reviewedAtPretty}
            />

            <div className="text-xs text-slate-400 space-y-1.5 border-y border-slate-800 py-3">
              <Meta icon={Calendar} label="Uploaded">
                {photo.uploaded_at
                  ? new Date(photo.uploaded_at).toLocaleString()
                  : "—"}
              </Meta>
              {photo.uploaded_by_name && (
                <Meta icon={User} label="By">{photo.uploaded_by_name}</Meta>
              )}
              {photo.gps_lat != null && photo.gps_lng != null && (
                <Meta icon={MapPin} label="GPS">
                  {photo.gps_lat.toFixed(5)}, {photo.gps_lng.toFixed(5)}
                </Meta>
              )}
              <Meta icon={() => null} label="Filename">
                <span className="font-mono text-[11px] break-all">
                  {photo.original_filename}
                </span>
              </Meta>
            </div>

            {!canReview && (
              <div className="text-xs text-slate-500 italic">
                Only Admins and Project Managers can approve or reject photos.
              </div>
            )}

            {canReview && (
              <div className="space-y-3 mt-auto">
                {/* Move-to-section picker takes over the side rail
                    when the admin/pm hits "Move" — same footprint as
                    the rejection form but with a category list. */}
                {movePickerOpen ? (
                  <div className="space-y-2" data-testid="photo-review-move-picker">
                    <div className="flex items-center justify-between">
                      <label className="text-[11px] uppercase tracking-wider text-slate-400 block">
                        Move to which section?
                      </label>
                      <button
                        type="button"
                        onClick={() => { setMovePickerOpen(false); setMovePicked(null); }}
                        className="text-slate-500 hover:text-slate-100 text-[11px] inline-flex items-center gap-1"
                        data-testid="photo-review-cancel-move"
                      >
                        <ArrowLeft className="w-3 h-3" /> Cancel
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
                      {(photoProgress?.categories || []).map((cat) => (
                        <div key={cat.name}>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold mb-1">
                            {cat.name}
                          </div>
                          <div className="grid grid-cols-1 gap-1">
                            {cat.items.map((it) => {
                              const currentSlot = it.key === photo.requirement_key;
                              const selected = movePicked?.key === it.key;
                              return (
                                <button
                                  key={it.key}
                                  type="button"
                                  disabled={currentSlot}
                                  onClick={() => setMovePicked({ category: cat.name, key: it.key, label: it.label })}
                                  className={`text-left p-2 rounded border text-xs ${
                                    currentSlot
                                      ? "bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed"
                                      : selected
                                        ? "bg-blue-950/40 border-blue-600 text-blue-100"
                                        : "bg-slate-950 border-slate-700 hover:border-slate-500 text-slate-200"
                                  }`}
                                  data-testid={`photo-review-move-item-${it.key}`}
                                >
                                  <div className="font-medium">{it.label}{currentSlot && " · current"}</div>
                                  {it.count > 0 && !currentSlot && (
                                    <div className="text-[10px] text-slate-500 mt-0.5 font-mono">{it.count} already here</div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {(photoProgress?.categories || []).length === 0 && (
                        <div className="text-sm text-slate-400 italic">
                          No template categories available. Set a photo template on the project first.
                        </div>
                      )}
                    </div>
                    <Button
                      className="w-full bg-red-700 hover:bg-red-600 font-display tracking-wider"
                      onClick={submitMove}
                      disabled={!movePicked || !!busy}
                      data-testid="photo-review-confirm-move"
                    >
                      {busy === "move" && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                      {movePicked ? `MOVE TO "${movePicked.label}"` : "PICK A DESTINATION"}
                    </Button>
                  </div>
                ) : rejectionOpen ? (
                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-wider text-slate-400 block">
                      What needs to be retaken?
                    </label>
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Glare on serial number — please retake from the side"
                      className="bg-slate-900 border-slate-700 min-h-[90px]"
                      data-testid="photo-review-reason"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => { setRejectionOpen(false); setReason(""); }}
                        disabled={!!busy}
                        data-testid="photo-review-cancel-reject"
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-red-600 hover:bg-red-500"
                        onClick={() => submit("rejected")}
                        disabled={!!busy || !reason.trim()}
                        data-testid="photo-review-confirm-reject"
                      >
                        {busy === "reject" ? (
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <X className="w-4 h-4 mr-1.5" />
                        )}
                        Send Retake Request
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {status !== "approved" && (
                      <Button
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 font-display tracking-wider"
                        onClick={() => submit("approved")}
                        disabled={!!busy}
                        data-testid="photo-review-approve"
                      >
                        {busy === "approve" ? (
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-1.5" />
                        )}
                        Approve
                      </Button>
                    )}
                    {status !== "rejected" && (
                      <Button
                        variant="outline"
                        className="flex-1 border-red-800 text-red-300 hover:bg-red-950/40 hover:text-red-200"
                        onClick={() => setRejectionOpen(true)}
                        disabled={!!busy}
                        data-testid="photo-review-reject"
                      >
                        <X className="w-4 h-4 mr-1.5" />
                        Reject
                      </Button>
                    )}
                  </div>
                )}

                {status !== "pending" && !rejectionOpen && !movePickerOpen && (
                  <Button
                    variant="ghost"
                    className="w-full text-slate-400 hover:text-slate-100"
                    onClick={() => submit("pending")}
                    disabled={!!busy}
                    data-testid="photo-review-revert"
                  >
                    {busy === "revert" ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Revert to pending
                  </Button>
                )}

                {!rejectionOpen && !movePickerOpen && (
                  <Button
                    variant="ghost"
                    className="w-full text-slate-400 hover:text-slate-100 border border-slate-800 hover:border-slate-600"
                    onClick={() => setMovePickerOpen(true)}
                    disabled={!!busy}
                    data-testid="photo-review-move"
                  >
                    <MoveRight className="w-3.5 h-3.5 mr-1.5" />
                    Move to different section
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-slate-500 shrink-0" />
      <div className="flex-1">
        <span className="text-slate-500">{label}: </span>
        <span className="text-slate-200">{children}</span>
      </div>
    </div>
  );
}

function ReviewStatusBanner({ status, reason, reviewedByName, reviewedAt }) {
  // Three visual states, color-coded so a foreman opening this dialog
  // mid-shift sees the decision at a glance without reading the copy.
  if (status === "approved") {
    return (
      <div
        className="rounded-md border border-emerald-700 bg-emerald-950/40 px-3 py-2 text-sm"
        data-testid="photo-review-status-banner"
      >
        <div className="flex items-center gap-2 font-display font-bold text-emerald-300">
          <Check className="w-4 h-4" /> Approved
        </div>
        {reviewedByName && (
          <div className="text-[11px] text-emerald-200/80 mt-0.5">
            by {reviewedByName}{reviewedAt ? ` · ${reviewedAt}` : ""}
          </div>
        )}
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div
        className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm"
        data-testid="photo-review-status-banner"
      >
        <div className="flex items-center gap-2 font-display font-bold text-red-300">
          <X className="w-4 h-4" /> Needs Retake
        </div>
        {reason && (
          <div className="text-[12px] text-red-100/90 mt-1 leading-snug">
            “{reason}”
          </div>
        )}
        {reviewedByName && (
          <div className="text-[11px] text-red-200/70 mt-0.5">
            by {reviewedByName}{reviewedAt ? ` · ${reviewedAt}` : ""}
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-400"
      data-testid="photo-review-status-banner"
    >
      <span className="font-display font-bold tracking-wider text-slate-300">
        Pending Review
      </span>
    </div>
  );
}
